import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { SortDirection } from '@app/classes';
import { SearchComponent } from '@app/components';
import { DefaultIconSize } from '@app/components/avatar/avatar.component';
import { ShapeIcons } from '@app/constants';
import { DroppableEventObject } from '@app/interfaces';
import { Column } from '@app/modules/table/classes/column.class';
import { Table } from '@app/modules/table/classes/table.class';
import { CellDisplay } from '@app/modules/table/enums/cell-display.enum';
import { InteractableColumn } from '@app/modules/table/interfaces/interactable-column.interface';
import { MenuColumnItem } from '@app/modules/table/interfaces/menu-column-item.interface';
import { TableOptions } from '@app/modules/table/interfaces/table-options.interface';
import { NotifyService } from '@app/services/notify.service';
import { breakPoints } from '@app/styles/theme';
import { DraggableData } from '@app/types';
import { TranslatableKey } from '@app/types/translatable.type';
import { ModelUnion as Model, RowChangeStatusTypes } from '@models/core/base.model';
import { TableMessageService } from '@table/classes/table-message.service';
import { TableMessagesEnum } from '@table/enums/table-messages.enum';
import { FilterValueChange } from '@table/interfaces/filter-value-change.interface';
import { TableDroppedData } from '@table/types/drag-drop.type';
import { css } from 'emotion';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import stickybits from 'stickybits';
import { Filter } from '../../classes/filter.abstract';
import { Action } from '../../interfaces/action.interface';
import { MenuColumnEvent } from '../../interfaces/menu-column-event.interface';
import { BulkSelection } from '../../types/bulk-selection.type';

type EventType<T extends Table> = ReturnType<T['getMenuColumnOptionsForRow']>[number]['event'];
type ModelType<T extends Table> = Parameters<T['getMenuColumnOptionsForRow']>[0];

const EMPTY_STATE_DEBOUNCE_TIME = 500;

// When a table is both responsive and has a sticky header the :host element (app-table) must have additional styles to allow scrolling
// Since this is conditional on the :host element, we add it in the component rather than in the .scss
const stickyResponsiveStyle = css`
    display: flex !important;
    flex-direction: column;
    overflow: hidden;
`;

/**
 *
 * @template TTable The type of table, used for outputs from event emitters. Must extend Table. Is inferred from table input.
 *
 *
 * NOTE: about the event click handler business.
 *
 * This is a bit of a hacky solution to the business requirement that if a filter is
 * open, and the user clicks anywhere else in the table, the filter should close.
 *
 * It uses regular DOM events. Anything with a "filter" class is a valid target to close
 * the currently active filter. Be wary when modifying the HTML/on click handlers here.
 *
 * <3 Mikey
 */
@Component({
    selector: 'app-table',
    templateUrl: './table.template.html',
    styleUrls: ['./table.style.scss'],
    animations: [
        trigger('fadeOut', [
            state('void', style({ opacity: 0 })),
            transition(':leave', [animate('5s 5s', style({ opacity: 0 }))]),
        ]),
        trigger('fadeIn', [
            state('void', style({ opacity: 0 })),
            transition(':enter', [animate('.35s .35s', style({ opacity: 1 }))]),
            transition(':leave', [animate('.35s .35s', style({ opacity: 0 }))]),
        ]),
    ],
})
export class TableComponent<TTable extends Table> implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('searchBar') searchBar: SearchComponent;

    @Input() showSearch = true;
    @Input() useStickyHeader = true;
    @Input() useStickyActions = false;
    @Input() showOffset = true;
    @Input() paddingOffset = 30;
    @Input() showActionsPadding = true;
    @Input() title: string;
    @Input() subTitle: string;
    @Input() options: TableOptions;

    /**
     * If this table is inside of a segment, enable this option to remove the shadows.
     * Ultimately, we want to consolidate the showOffsetBar property into this as well, so a one toggle fixes all
     */
    @Input() embedded = false;

    @Input() showPagination = true;
    @Input() paginationPosition: 'top' | 'bottom' = 'top';
    @Input() isSmall = false;
    @Input() showHeader = true;
    @Input() readonly = false;
    @Input() noWhiteSpace = false;
    @Input() noPadding = false;
    @Input() searchPlaceholder: TranslatableKey = 'search';
    @Input() searchWidth = 250;

    /**
     * showLoading is used to show the loading spinner when the table is loading data.
     * You might want to turn this off if you are using the table in a component that
     * has its own loading spinner.
     *
     * See `onLoading` on the table class to subscribe to the loading event.
     */
    @Input() showLoading = true;
    @Input() isResponsive = false;

    @Output() menuItemSelected: EventEmitter<MenuColumnEvent<ModelType<TTable>, EventType<TTable>>> = new EventEmitter<
        MenuColumnEvent<ModelType<TTable>, EventType<TTable>>
    >();
    @Output() action: EventEmitter<Action> = new EventEmitter<Action>();
    @Output() rowClicked: EventEmitter<Parameters<TTable['getLink']>[0]> = new EventEmitter<Model>();
    @Output() rowSelected: EventEmitter<Model> = new EventEmitter<Model>();
    @Output() rowDeselected: EventEmitter<Model> = new EventEmitter<Model>();
    @Output() rowChanged: EventEmitter<Model> = new EventEmitter<Model>();
    @Output() selectionChanged: EventEmitter<BulkSelection> = new EventEmitter<BulkSelection>();
    @Output() dropdownSelectionChanged: EventEmitter<{ model: Model; column: Column }> = new EventEmitter<{
        model: Model;
        column: Column;
    }>();
    @Output() allFiltersCleared: EventEmitter<void> = new EventEmitter<void>();
    @Output() filterChanged: EventEmitter<FilterValueChange> = new EventEmitter<FilterValueChange>();
    @Output() searchQueryChanged: EventEmitter<string> = new EventEmitter<string>();

    @Output() dropEvent: EventEmitter<TableDroppedData> = new EventEmitter<TableDroppedData>();
    @Output() rowDragEvent: EventEmitter<DroppableEventObject> = new EventEmitter<DroppableEventObject>();

    cellDisplay = CellDisplay;
    SortDirections = SortDirection;
    showFilterColumn?: Column | null;

    isLoading = true;
    isOpen = false;
    showEmptyState = false;
    hasFilterableColumns = false;
    hasOptionsColumn = false;

    bulkSelection: BulkSelection = {
        isAllSelected: false,
        selectedIds: new Set<number>(),
        deselectedIds: new Set<number>(),
        selectionCount: 0,
    };

    eventIcons: { [key: string]: (typeof ShapeIcons)[number]['name'] } = {
        download: 'download',
        remind: 'notification',
        edit: 'edit',
        complete: 'complete',
        revoke: 'cancel',
    };

    rowChangeStatusTypes = RowChangeStatusTypes;

    defaultIconSize = DefaultIconSize;

    protected unsubscribeAll: Subject<any> = new Subject<any>();

    private _table: TTable;
    private bulkSelectedRows: Model[] = [];
    private messagesSubscription: Subscription;
    private _isMobile?: boolean;

    constructor(
        protected router: Router,
        private notify: NotifyService,
        protected element: ElementRef<HTMLElement>,
        protected messages: TableMessageService,
        private breakpointObserver: BreakpointObserver
    ) {}

    get isMobile(): boolean {
        const isMobile = !this.breakpointObserver.isMatched(`(min-width: ${breakPoints.lg}px)`);

        // If the screen is switching between the mobile view and the desktop view the sticky offsets need to be recalculated since the header position changes
        if (this.useStickyHeader && this._isMobile !== undefined && this._isMobile !== isMobile) {
            // setTimeout is required here because the DOM update won't actually occur until AFTER this function returns, therefore the correct offsets will be available on the next change detection cycle
            setTimeout(() => {
                this.calculateStickyOffsets();
            });
        }
        this._isMobile = isMobile;
        return this._isMobile;
    }

    @Input() set table(val: TTable) {
        this._table = val;
        this.table.onLoading
            .pipe(takeUntil(this.unsubscribeAll), debounceTime(EMPTY_STATE_DEBOUNCE_TIME))
            .subscribe((loading) => {
                this.showEmptyState = !loading && this.table.paginator.empty;
                if (!this.showEmptyState) {
                    this.hasOptionsColumn = this.table.paginator.values.some(
                        (row) => this.table.getMenuColumnOptionsForRow(row)?.length
                    );
                }
                return this.showEmptyState;
            });
        this.hasFilterableColumns = this.table.columns.some((col) => col.filter);

        this.table.onFilterChanged.pipe(takeUntil(this.unsubscribeAll)).subscribe((change: FilterValueChange) => {
            this.filterChanged.emit(change);
        });
    }

    get table(): TTable {
        return this._table;
    }

    get showMultiSelectFilter(): boolean {
        return this.isResponsive && this.isMobile;
    }

    @HostListener('document:keydown.escape', ['$event'])
    onKeydownHandler(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.showFilterColumn = null;
        }
    }

    /**
     * Draggable data on app table is normally just the row model, however on multi-select we
     * grab all the currently checked models
     */
    getDraggedData(row: Model): DraggableData | DraggableData[] | null {
        if (!this.table.allowDragAndDrop) {
            return null;
        }
        if (this.bulkSelection.isAllSelected || this.bulkSelection.selectedIds.size) {
            return this.bulkSelectedRows;
        }
        return row;
    }

    /**
     * When a single row is dragged, we auto check the checkbox (if enabled)
     */
    dragStart(dragged: DroppableEventObject): void {
        if (this.table.bulkSelect && !Array.isArray(dragged.data)) {
            this.onRowSelected(dragged.data as unknown as Model);
        }
        this.rowDragEvent.emit({ data: dragged });
    }

    isRowValidDropZone(model: Model): boolean {
        return this.table.isRowValidDropZone(model);
    }

    /**
     * This method is used to determine the current `modelId` being dragged and emit it to parent
     * along with its new `targetId`.
     */
    onDrop(dropped: { data: DraggableData | DraggableData[] }, target): void {
        this.dropEvent.emit({ dropped: dropped.data, target: target });
    }

    onRowClicked(row: Model, event: Event): void {
        event.stopPropagation();

        if (this.table.links && !this.table.emitRowClickedWithLinksEnabled) {
            return;
        }

        this.rowClicked.emit(row);
    }

    isRowChecked(row: Model): boolean {
        return (
            (this.bulkSelection.isAllSelected && !this.bulkSelection.deselectedIds.has(row.id)) ||
            (!this.bulkSelection.isAllSelected && this.bulkSelection.selectedIds.has(row.id))
        );
    }

    onAllSelectChanged(checked: boolean): void {
        this.bulkSelection.selectedIds.clear();
        this.bulkSelection.deselectedIds.clear();

        this.bulkSelection.isAllSelected = checked;
        this.bulkSelection.selectionCount = this.getSelectionCount();
        this.bulkSelection.filters = this.table.paginator.currentQuery?._filters;
        this.selectionChanged.emit(this.bulkSelection);
    }

    onRowSelectChanged(row: Model, checked: boolean): void {
        checked ? this.onRowSelected(row) : this.onRowDeselected(row);
    }

    clearSelection(): void {
        this.bulkSelection.selectedIds.clear();
        this.bulkSelection.deselectedIds.clear();
        this.bulkSelection.isAllSelected = false;
    }

    onDropdownSelection(model: Model, column: Column): void {
        this.dropdownSelectionChanged.emit({ model, column });
    }

    onSearch(query: string): void {
        this.table.paginator.search = query;

        this.searchQueryChanged.emit(query);
    }

    applyStyles(): void {
        if (this.useStickyHeader && this.isResponsive) {
            this.element.nativeElement.classList.add(stickyResponsiveStyle);
        }
    }

    ngOnInit(): void {
        this.applyStyles();

        // The filter component has 'filter' on all of its clickable elements. Sort of a hack, but the simplest way to get it to work.

        this.table.onLoading.pipe(takeUntil(this.unsubscribeAll)).subscribe((loading) => {
            this.isLoading = loading;
        });

        /**
         * When multi-select is updated, keep a cache of selected models - used for drag/drop. Having a cache
         * of the models allows us to avoid memory issues with function calls in bound attributes.
         */
        this.selectionChanged.subscribe(() => {
            this.bulkSelectedRows = this.table.paginator.values.filter((model) => this.isRowChecked(model));
        });

        this.subscribeToMessages();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next(true);
        this.messagesSubscription?.unsubscribe();
    }

    ngAfterViewInit(): void {
        if (this.useStickyHeader) {
            this.calculateStickyOffsets();
        }
    }

    calculateStickyOffsets(): void {
        // The non-responsive table has an additional 24px of padding on the .actions element, whereas the responsive table adds this padding to the table itself
        // This 24px needs to be subtracted from all of the offsets if the table is responsive.
        const responsiveOffset = this.isResponsive ? -24 : 0;
        // Offset bar adjustment to account for negative positioning
        // Otherwise, a small sliver of the table shows beneath it
        const offsetBarNegativeOffset = responsiveOffset + -8;
        // Padding offset
        const offset = responsiveOffset + this.paddingOffset;
        // Action bar size
        const actionBarHeight = responsiveOffset + 88;
        const thHeight = this.hasFilterableColumns ? 53 : 49; // The filter icon causes the height of th element to expand
        stickybits('.offset-bar', { useStickyClasses: true, stickyBitStickyOffset: offsetBarNegativeOffset });
        stickybits('.actions.sticky', { stickyBitStickyOffset: offset });
        stickybits('th.sticky', { useStickyClasses: true, stickyBitStickyOffset: actionBarHeight + offset });
        stickybits('th.bottom-border-row', { stickyBitStickyOffset: actionBarHeight + offset + thHeight });
    }

    onNavigate(row: Model): void {
        if (this.table.links) {
            this.router.navigate([...this.table.getLink(row), row.id]);
        }
    }

    onSort(column: Column, event: Event): void {
        event.stopPropagation();
        this.table.sort(column);
    }

    onShowFilter(column: Column): void {
        if (this.showFilterColumn === column) {
            this.showFilterColumn = null;
        }

        this.showFilterColumn = column;
    }

    async onRowChange(column: InteractableColumn, row: Model): Promise<void> {
        try {
            await column.onRowChange(row);
            this.rowChanged.emit(row);
        } catch (e) {
            this.notify.error(e);
        } finally {
            setTimeout(() => {
                $(this.element.nativeElement).find('.save-progress-icon').fadeOut();
            }, 5000);
        }
    }

    getLink(row: Model): (string | number)[] | null {
        return this.table.links ? this.table.getLink(row) : null;
    }

    getParams(row: Model): any {
        return this.table.getParams(row);
    }

    onMenuItemSelected(menuItem: MenuColumnItem<EventType<TTable>>, data: ModelType<TTable>): void {
        this.menuItemSelected.emit({ menuItem, data });
    }

    onAction(event: string, data: Model | object): void {
        this.action.emit({ event, data });
    }

    onClearFilters(): void {
        this.allFiltersCleared.emit();

        this.table.clearFilters();

        if (this.searchBar) {
            this.searchBar.clear();
        }
    }

    trackByFunction(_index: number, item: any): null | any {
        if (!item) {
            return null;
        }

        return item.id || item.value;
    }

    handleFilterBackdropClick(): void {
        this.showFilterColumn = null;
    }

    isRowDisabled(row: Model): boolean {
        return row.hasAttribute?.('disabled');
    }

    // Concatenates table and column filters into a single array for the multi-filter component
    getAllFilters(): Filter[] {
        const columnFilters = this.table.columns
            .map((column) => column.filter)
            .filter((filter) => filter !== undefined);
        return [...this.table.filters, ...columnFilters];
    }

    // Column widths are given as a string (ie. 16.66667%). We need to parse these and make them into a decimal for usage within styles (ie. 0.1666667)
    percentageStringToDecimal(percentage: string): number {
        return parseFloat(percentage.slice(0, -1)) / 100;
    }

    private onRowSelected(row: Model): void {
        if (!this.bulkSelection.isAllSelected) {
            this.bulkSelection.selectedIds.add(row.bulkSelectId);
        }

        this.bulkSelection.deselectedIds.delete(row.bulkSelectId);
        this.rowSelected.emit(row);
        this.bulkSelection.selectionCount = this.getSelectionCount();
        this.bulkSelection.filters = this.table.paginator.currentQuery?._filters;
        this.selectionChanged.emit(this.bulkSelection);
    }

    private onRowDeselected(row: Model): void {
        if (this.bulkSelection.isAllSelected) {
            this.bulkSelection.deselectedIds.add(row.bulkSelectId);
        }

        this.bulkSelection.selectedIds.delete(row.bulkSelectId);
        this.rowDeselected.emit(row);
        this.bulkSelection.selectionCount = this.getSelectionCount();
        this.bulkSelection.filters = this.table.paginator.currentQuery?._filters;
        this.selectionChanged.emit(this.bulkSelection);
    }

    private getSelectionCount(): number {
        if (!this.bulkSelection.isAllSelected) {
            return this.bulkSelection.selectedIds.size;
        }

        return this.table.paginator.total - this.bulkSelection.deselectedIds.size;
    }

    /**
     * If we need to update the table based on some event, we can pass a message via this subscription.
     * To add a message, add to TableMessagesEnum and inject the TableMessageService to your calling class.
     *
     * Example use case - Table class needs to make AppTable clear multi-select selections when updating query.
     */
    private subscribeToMessages(): void {
        this.messagesSubscription = this.messages.subject.subscribe((message: TableMessagesEnum) => {
            switch (message) {
                case TableMessagesEnum.CLEAR_SELECTION: {
                    this.clearSelection();
                }
            }
        });
    }
}
